// v3 `stores/integrationStore.ts:12-56` (`channelMappings`) — 43 short aliases → canonical channel
// key. Contract §3 moves it here: it is registry data that the store merely happened to hold.
//
// The aliases are what other surfaces call a channel (a report's `channel` column, a nav param, a
// permission string), not what the integrations wire calls it. Several map to the same target
// (`apple_search`/`apple_search_ads`, `analytics`/`ga4`, `dv360`/`dv360_advertisers`,
// `meta_catalogs`/`meta_catalog`) and several are identity entries kept so a caller can resolve
// blindly without first asking whether its input is already canonical.

/** Alias → canonical channel key (a provider `id`, or a display-only channel). */
export const CHANNEL_ALIASES: Readonly<Record<string, string>> = {
  akakce: 'akakce_account',
  adjust: 'adjust_account',
  apple_search: 'apple_search_ads_account',
  apple_search_ads: 'apple_search_ads_account',
  applovin_max: 'applovin_max_account',
  app_store: 'app_store',
  appsflyer: 'appsflyer_account',
  bq: 'bigquery_account',
  bing: 'bing_ads_account',
  cm360: 'cm360_advertiser',
  criteo: 'criteo_account',
  dv360: 'dv360_advertiser',
  dv360_advertisers: 'dv360_advertiser',
  meta: 'meta_ads_account',
  cpas: 'meta_cpas_account',
  meta_catalogs: 'meta_catalog',
  meta_catalog: 'meta_catalog',
  meta_pixel: 'meta_pixel',
  facebook_page: 'facebook_page',
  analytics: 'ga4_analytics_property',
  ga4: 'ga4_analytics_property',
  gelir: 'gelirortaklari_account',
  google: 'google_ads_account',
  // v3 `integrationStore.ts:47-48`: the auth card's display name and the channel's own key.
  tag_manager: 'gtm_container',
  gtm_container: 'gtm_container',
  play_store: 'playstore_account',
  hardal: 'hardal_account',
  ikas: 'ikas_account',
  linkedin: 'linkedin_ads_account',
  pinterest: 'pinterest_account',
  reddit: 'reddit_ad_account',
  reklam_store: 'reklamstore_account',
  rtb: 'rtb_account',
  search_console: 'search_console',
  shopify: 'shopify_store',
  singular: 'singular_account',
  snapchat: 'snapchat_account',
  ticimax: 'ticimax_account',
  tiktok: 'tiktok_ads_account',
  tiktok_pixel: 'tiktok_pixel',
  tiktok_catalog: 'tiktok_catalog',
  twitter: 'twitter_ad_account',
  yandex: 'yandex_direct_account',
  merchant_center: 'merchant_center_account',
  yango: 'yango_account',
  tsoft: 'tsoft_account',
  youtube: 'youtube_channel',
  openai_ads: 'openai_ads_account',
}

/**
 * The canonical channel key behind an alias.
 *
 * Falls back to the input so a caller that already holds a canonical key gets it back unchanged —
 * v3 callers all wrote `channelMappings[x] || x` at the call site.
 */
export function resolveChannelKey(alias: string): string {
  return CHANNEL_ALIASES[alias] ?? alias
}
